import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Col, Form, InputNumber, message, Modal, Row, Select } from 'antd';
import styled from 'styled-components';

import { AxiosResponse } from 'axios';
import Input from 'pages/employees/components/Input/Input';
import PhoneNumberInput from 'pages/employees/components/Input/InputPhone';
import {
  IStaffItemData,
  STAFF_POSITION_INFO,
} from 'pages/account/SalonEmployees';
import { IUpdateStaff } from 'pages/employees/services/types';
import accountApis from 'services/accountApis';
import InputDOB from 'components/InputDOB';

interface Props {}

type FormValues = IStaffItemData;

export interface UpdateStaffModalRef {
  open: (data?: IStaffItemData, onSuccess?: () => void) => void;
  close: () => void;
}

const UpdateStaffModal = forwardRef<UpdateStaffModalRef, Props>(({}, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IStaffItemData>();
  const [loading, setLoading] = useState(false);
  const refSuccess = useRef<() => void>();

  const onOpen = (data?: IStaffItemData, onSuccess?: () => void) => {
    setOpen(true);
    if (data) {
      setData(data);
      form.setFieldsValue(data);
    }
    refSuccess.current = onSuccess;
  };

  const onClose = () => {
    setOpen(false);
    setData(undefined);
    form.resetFields();
  };

  const onFinish = async (values: FormValues) => {
    try {
      setLoading(true);
      const body: IUpdateStaff = {
        staffId: data?.id || '',
        avatar: values.urlImage,
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        phone: values.phone || '',
        email: values.email || '',
        dob: values.birthday || '',
        staffType: values.staffType || STAFF_POSITION_INFO.TECHNICAL,
        street: values.address || '',
        city: values.city || '',
        state: values.state || '',
        zip: values.zip || '',
        status: data?.status || 'ACTIVE',
        pinCode: String(values.password) || '',
      };
      const response: AxiosResponse<{ data: any }> =
        await accountApis.updateStaff(body);

      if (response.data.data) {
        message.success('Staff updated successfully');
        onClose();
        refSuccess.current?.();
      } else {
        message.error('Failed to update staff');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onDone = () => {
    form.submit();
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const optionsPosition = [
    { value: STAFF_POSITION_INFO.ADMIN, label: 'Admin' },
    { value: STAFF_POSITION_INFO.MANAGER, label: 'Manager' },
    { value: STAFF_POSITION_INFO.CASHIER, label: 'Cashier' },
    { value: STAFF_POSITION_INFO.TECHNICAL, label: 'Technical' },
  ];

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={800}
      centered
      destroyOnClose={false}
      closable={false}
    >
      <ContainerStyled>
        <TitleStyled>{data?.id ? 'Update' : 'Add'} Staff</TitleStyled>
        <Form form={form} onFinish={onFinish} layout='vertical'>
          <Row gutter={[16, 8]}>
            {/* <Col span={24}>
              <Form.Item name='urlImage' noStyle>
                <StaffAvatarForm />
              </Form.Item>
            </Col> */}

            <Col span={12}>
              <Form.Item
                required={false}
                name='firstName'
                label={
                  <span>
                    First Name <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter staff name' }]}
              >
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required={false}
                name='lastName'
                label={
                  <span>
                    Last Name <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter employee name' },
                ]}
              >
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required={false}
                name='phone'
                label={
                  <span>
                    Phone <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter staff phone' },
                ]}
              >
                <PhoneNumberInput placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required={false}
                name='email'
                label={<span>Email</span>}
                rules={[
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                ]}
              >
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='password'
                label={<span>Password <span style={{ color: 'red' }}>*</span></span>}
                rules={[{ required: true, message: 'Please enter staff password' }]}
              >
                <InputNumber placeholder='Please enter...' controls={false} maxLength={4}/>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='birthday'
                label={<span>DOB</span>}
              >
                <InputDOB />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='staffType'
                label={<span>Position</span>}
              >
                <Select
                  options={optionsPosition}
                  style={{ width: '100%', height: '48px', borderRadius: '4px' }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name='address' label='Address'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='city' label='City'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='state' label='State'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='zip' label='Zip Code'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <ButtonsStyled>
          <button type='button' className='btn-cancel' onClick={onClose}>
            Close
          </button>
          <button
            type='button'
            className='btn-submit'
            onClick={onDone}
            disabled={loading}
          >
            Ok
          </button>
        </ButtonsStyled>
      </ContainerStyled>
    </Modal>
  );
});
UpdateStaffModal.displayName = 'UpdateStaffModal';

export default UpdateStaffModal;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .ant-form-item {
    margin-bottom: 8px;
  }

  .ant-select-selector {
    border-radius: 4px;

    .ant-select-selection-item {
      font-size: 16px;
      font-weight: 500;
    }
  }

  .ant-input-number {
    width: 100%;
    height: 48px;
    border-radius: 4px;
    .ant-input-number-input {
      height: 48px;
      font-size: 16px;
      font-weight: 500;
    }
  }
`;

const TitleStyled = styled.div`
  align-self: stretch;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonsStyled = styled.div`
  display: flex;
  gap: 16px;

  button {
    cursor: pointer;
    display: flex;
    height: 48px;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;

    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    &.btn-cancel {
      background: #e3e9ed;
      color: #566a7f;
    }
    &.btn-submit {
      color: #fff;
      background: #ff8890;
    }
    &:hover {
      opacity: 0.8;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
